'use client';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { supabase } from '@/lib/supabase';

interface Category {
  id: string;
  name: string;
  icon_url: string | null;
}

interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  original_price: number | null;
  stock_count: number;
  seller_id: string;
  category_id: string | null;
  primary_image_url: string | null;
  rating: number | null;
  review_count: number | null;
  is_active: boolean;
  store_name?: string;
}

interface CartItem {
  product_id: string;
  quantity: number;
}

export default function ShopContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('cat') || '');
  const [sortBy, setSortBy] = useState('newest');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(new Set());
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadCart = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from('cart_items')
      .select('product_id, quantity')
      .eq('user_id', uid);
    setCartItems(data || []);
  }, []);

  const loadWishlist = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from('wishlist_items')
      .select('product_id')
      .eq('user_id', uid);
    setWishlistIds(new Set((data || []).map((w: any) => w.product_id)));
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setUserId(user.id);
        loadCart(user.id);
        loadWishlist(user.id);
      }
    });
  }, [loadCart, loadWishlist]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('products')
        .select('id, name, description, price, original_price, stock_count, seller_id, category_id, primary_image_url, rating, review_count, is_active')
        .eq('is_active', true)
        .gt('stock_count', 0);

      if (selectedCategory) query = query.eq('category_id', selectedCategory);
      if (searchQuery.trim()) query = query.ilike('name', `%${searchQuery.trim()}%`);

      if (sortBy === 'price_asc') query = query.order('price', { ascending: true });
      else if (sortBy === 'price_desc') query = query.order('price', { ascending: false });
      else if (sortBy === 'rating') query = query.order('rating', { ascending: false, nullsFirst: false });
      else query = query.order('created_at', { ascending: false });

      const { data } = await query.limit(60);
      setProducts((data || []).map((p: any) => ({ ...p, store_name: 'Bozaat Store' })));
    } catch (err) {
      console.error('Products load error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery, sortBy]);

  useEffect(() => {
    supabase.from('categories').select('id, name, icon_url').order('name').then(({ data }) => {
      setCategories(data || []);
    });
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Real-time subscription: reflect seller product changes in shop instantly
  useEffect(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    channelRef.current = supabase
      .channel('shop-products-rt')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'products',
      }, (payload) => {
        const newProduct = payload.new as Product;
        // Only show if active and in stock, and matches current filters
        if (!newProduct.is_active || newProduct.stock_count <= 0) return;
        if (selectedCategory && newProduct.category_id !== selectedCategory) return;
        if (searchQuery.trim() && !newProduct.name.toLowerCase().includes(searchQuery.trim().toLowerCase())) return;
        setProducts(prev => {
          if (prev.some(p => p.id === newProduct.id)) return prev;
          return [{ ...newProduct, store_name: 'Bozaat Store' }, ...prev];
        });
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'products',
      }, (payload) => {
        const updated = payload.new as Product;
        setProducts(prev => {
          // If product becomes inactive or out of stock, remove from list
          if (!updated.is_active || updated.stock_count <= 0) {
            return prev.filter(p => p.id !== updated.id);
          }
          // If it was not in list but now qualifies, add it
          const exists = prev.some(p => p.id === updated.id);
          if (!exists) {
            if (selectedCategory && updated.category_id !== selectedCategory) return prev;
            return [{ ...updated, store_name: 'Bozaat Store' }, ...prev];
          }
          return prev.map(p => p.id === updated.id ? { ...p, ...updated } : p);
        });
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'products',
      }, (payload) => {
        const deleted = payload.old as { id: string };
        setProducts(prev => prev.filter(p => p.id !== deleted.id));
      })
      .subscribe();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [selectedCategory, searchQuery]);

  const handleAddToCart = async (product: Product) => {
    if (!userId) { router.push('/customer-login'); return; }
    setAddingToCart(product.id);
    try {
      const existing = cartItems.find(c => c.product_id === product.id);
      if (existing) {
        await supabase.from('cart_items').update({ quantity: existing.quantity + 1 }).eq('user_id', userId).eq('product_id', product.id);
      } else {
        await supabase.from('cart_items').insert({ user_id: userId, product_id: product.id, quantity: 1, added_at: new Date().toISOString() });
      }
      await loadCart(userId);
      showToast('Added to cart!', 'success');
    } catch {
      showToast('Failed to add to cart', 'error');
    } finally {
      setAddingToCart(null);
    }
  };

  const handleToggleWishlist = async (productId: string) => {
    if (!userId) { router.push('/customer-login'); return; }
    const isWishlisted = wishlistIds.has(productId);
    if (isWishlisted) {
      await supabase.from('wishlist_items').delete().eq('user_id', userId).eq('product_id', productId);
      setWishlistIds(prev => { const s = new Set(prev); s.delete(productId); return s; });
    } else {
      await supabase.from('wishlist_items').insert({ user_id: userId, product_id: productId, added_at: new Date().toISOString() });
      setWishlistIds(prev => new Set([...prev, productId]));
      showToast('Added to wishlist', 'success');
    }
  };

  const cartCount = cartItems.reduce((s, c) => s + c.quantity, 0);
  const discount = (p: Product) => p.original_price && p.original_price > p.price ? Math.round(((p.original_price - p.price) / p.original_price) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium max-w-sm ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={18} />
          <span className="flex-1">{toast.msg}</span>
          <button onClick={() => setToast(null)}><Icon name="XMarkIcon" size={14} /></button>
        </div>
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Link href="/" className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">B</span>
              </div>
              <span className="font-bold text-slate-900 text-lg hidden sm:block">Bozaat</span>
            </Link>
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadProducts()}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border border-transparent rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary focus:bg-white transition-all"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {userId ? (
                <Link href="/customer-dashboard" className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
                  <Icon name="UserCircleIcon" size={22} className="text-slate-600" />
                </Link>
              ) : (
                <Link href="/customer-login" className="text-sm font-medium text-primary hover:underline">Sign In</Link>
              )}
              <Link href="/cart" className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors">
                <Icon name="ShoppingCartIcon" size={22} className="text-slate-600" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center">{cartCount > 9 ? '9+' : cartCount}</span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
          <button onClick={() => setSelectedCategory('')} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${!selectedCategory ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary hover:text-primary'}`}>
            All Products
          </button>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setSelectedCategory(selectedCategory === cat.id ? '' : cat.id)} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === cat.id ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary hover:text-primary'}`}>
              {cat.name}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-slate-500">{loading ? 'Loading...' : `${products.length} product${products.length !== 1 ? 's' : ''} found`}</p>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30">
            <option value="newest">Newest First</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="rating">Top Rated</option>
          </select>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-slate-200" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-slate-200 rounded w-3/4" />
                  <div className="h-3 bg-slate-200 rounded w-1/2" />
                  <div className="h-8 bg-slate-200 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="ShoppingBagIcon" size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-600 font-medium">No products found</p>
            <p className="text-sm text-slate-400 mt-1">Try a different search or category</p>
            {(searchQuery || selectedCategory) && (
              <button onClick={() => { setSearchQuery(''); setSelectedCategory(''); }} className="mt-4 text-sm text-primary hover:underline">Clear filters</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {products.map(product => {
              const disc = discount(product);
              const inCart = cartItems.some(c => c.product_id === product.id);
              const isWishlisted = wishlistIds.has(product.id);
              return (
                <div key={product.id} className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-md transition-shadow group">
                  <Link href={`/shop/${product.id}`} className="block relative aspect-square bg-slate-50 overflow-hidden">
                    {product.primary_image_url ? (
                      <AppImage
                        src={product.primary_image_url}
                        alt={product.name}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="PhotoIcon" size={32} className="text-slate-300" />
                      </div>
                    )}
                    {disc > 0 && (
                      <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{disc}% OFF</span>
                    )}
                    <button
                      onClick={(e) => { e.preventDefault(); handleToggleWishlist(product.id); }}
                      className="absolute top-2 right-2 w-7 h-7 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors"
                    >
                      <Icon name={isWishlisted ? 'HeartIcon' : 'HeartIcon'} size={14} className={isWishlisted ? 'text-red-500' : 'text-slate-400'} />
                    </button>
                  </Link>
                  <div className="p-3">
                    <Link href={`/shop/${product.id}`}>
                      <p className="text-sm font-semibold text-slate-900 line-clamp-2 hover:text-primary transition-colors leading-tight mb-1">{product.name}</p>
                    </Link>
                    {product.rating && (
                      <div className="flex items-center gap-1 mb-1.5">
                        <Icon name="StarIcon" size={11} className="text-amber-400" />
                        <span className="text-xs text-slate-500">{product.rating.toFixed(1)}</span>
                        {product.review_count && <span className="text-xs text-slate-400">({product.review_count})</span>}
                      </div>
                    )}
                    <div className="flex items-baseline gap-1.5 mb-2.5">
                      <span className="font-bold text-slate-900 text-sm">₹{product.price.toLocaleString('en-IN')}</span>
                      {product.original_price && product.original_price > product.price && (
                        <span className="text-xs text-slate-400 line-through">₹{product.original_price.toLocaleString('en-IN')}</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={addingToCart === product.id}
                      className={`w-full py-2 rounded-xl text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                        inCart
                          ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100' :'bg-primary text-white hover:bg-primary/90'
                      } disabled:opacity-60`}
                    >
                      {addingToCart === product.id ? (
                        <Icon name="ArrowPathIcon" size={13} className="animate-spin" />
                      ) : inCart ? (
                        <>
                          <Icon name="CheckIcon" size={13} />
                          In Cart
                        </>
                      ) : (
                        <>
                          <Icon name="ShoppingCartIcon" size={13} />
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
